import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import DramaCard from '@/components/base/DramaCard';
import { dramas } from '@/mocks/dramas';

export default function DramaDetailPage() {
  const { id } = useParams<{ id: string }>();

  const drama = dramas.find((d) => d.id === id);

  const relatedDramas = drama
    ? dramas
        .filter((d) => d.categorySlug === drama.categorySlug && d.id !== drama.id)
        .slice(0, 6)
    : [];

  useEffect(() => {
    if (drama) {
      document.title = `${drama.title} - 香蕉影视高清在线观看`;
    } else {
      document.title = '剧集未找到 - 香蕉影视';
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [drama]);

  if (!drama) {
    return (
      <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-20 text-center">
        <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-secondary-100 text-secondary-500">
          <i className="ri-emotion-sad-line text-2xl"></i>
        </div>
        <h2 className="text-lg font-bold text-foreground-900 mb-2">剧集未找到</h2>
        <p className="text-sm text-foreground-500 mb-4">抱歉，您查找的剧集不存在或已下架</p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i>
          返回首页
        </Link>
      </div>
    );
  }

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://banana-tv.com/' },
              {
                '@type': 'ListItem',
                position: 2,
                name: drama.category,
                item: `https://banana-tv.com/category/${drama.categorySlug}`,
              },
              { '@type': 'ListItem', position: 3, name: drama.title },
            ],
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: `${drama.title} - 香蕉影视高清在线观看`,
            description: drama.description,
            author: { '@type': 'Person', name: drama.director },
            datePublished: `${drama.year}-01-01`,
            publisher: {
              '@type': 'Organization',
              name: '香蕉影视',
            },
          }),
        }}
      />

      <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-6">
        <Breadcrumb
          items={[
            { label: drama.category, href: `/category/${drama.categorySlug}` },
            { label: drama.title },
          ]}
        />

        <div className="flex flex-col md:flex-row gap-6 md:gap-8">
          <div className="w-full md:w-[300px] shrink-0">
            <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-100">
              <img
                src={drama.image}
                alt={drama.title}
                title={`${drama.title} - 香蕉影视高清在线观看`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="flex-1">
            <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-2">
              {drama.title}
            </h1>

            <div className="flex items-center gap-3 flex-wrap mb-4">
              <span className="flex items-center gap-1 text-sm font-bold text-accent-500">
                <i className="ri-star-fill"></i>
                {drama.rating}
              </span>
              <span className="text-sm text-foreground-500">{drama.year}</span>
              <span className="text-sm text-foreground-500">{drama.region}</span>
              <span className="text-sm text-foreground-500">{drama.episodes}</span>
              <span className={`px-2 py-0.5 text-xs rounded-full font-medium whitespace-nowrap ${
                drama.status === '热播中' || drama.status === '连载中'
                  ? 'bg-primary-100 text-primary-600'
                  : 'bg-secondary-100 text-secondary-600'
              }`}>
                {drama.status}
              </span>
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
              {drama.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-2.5 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>

            <div className="mb-4">
              <h3 className="text-sm font-semibold text-foreground-800 mb-1.5">导演</h3>
              <p className="text-sm text-foreground-600">{drama.director}</p>
            </div>

            <div className="mb-4">
              <h3 className="text-sm font-semibold text-foreground-800 mb-1.5">主演</h3>
              <p className="text-sm text-foreground-600">{drama.actors.join(' / ')}</p>
            </div>

            <div className="mb-6">
              <h3 className="text-sm font-semibold text-foreground-800 mb-1.5">剧情简介</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">{drama.description}</p>
            </div>

            <div className="flex items-center gap-3">
              <button className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 hover:bg-primary-600 text-white font-medium text-sm transition-all duration-300 whitespace-nowrap cursor-pointer">
                <i className="ri-play-circle-line text-lg"></i>
                立即观看
              </button>
              <button className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-secondary-100 hover:bg-secondary-200 text-foreground-700 text-sm transition-all duration-300 whitespace-nowrap cursor-pointer">
                <i className="ri-heart-line"></i>
                收藏
              </button>
            </div>
          </div>
        </div>

        {relatedDramas.length > 0 && (
          <section className="mt-12 pt-8 border-t border-background-200">
            <div className="mb-5">
              <h2 className="font-heading text-lg font-bold text-foreground-900">
                同类推荐
              </h2>
              <p className="text-sm text-foreground-500 mt-1">你可能也喜欢这些剧集</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-5">
              {relatedDramas.map((d) => (
                <DramaCard key={d.id} drama={d} />
              ))}
            </div>
          </section>
        )}
      </div>
    </>
  );
}